import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isValid,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { CalendarClockIcons } from '../../../icons/icons';
import { ChevronLeft, ChevronRight } from '../../icons/icons';

type DatePickerProps = {
  label?: string;
  startDate: Date | string | number | null;
  endDate?: Date | string | number | null;
  onChange: (timeRange: {
    startDate: string | null;
    endDate: string | null;
  }) => void;
  disabled?: boolean;
  asSingle?: boolean;
  useRange?: boolean;
};

const parseDateOnlyString = (value: string): Date | null => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const [, yearStr, monthStr, dayStr] = match;
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  const date = new Date(year, month - 1, day);

  if (
    !isValid(date) ||
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
};

export const DatePicker = ({
  label = '',
  startDate,
  endDate,
  onChange,
  disabled,
  asSingle = false,
  useRange = true,
}: DatePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] =
    useState<'calendar' | 'month' | 'year'>('calendar');
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });

  const toDate = (val: Date | string | number | null | undefined): Date => {
    if (!val || val === 0) return new Date();
    if (val instanceof Date) return isValid(val) ? val : new Date();
    let date: Date;
    if (typeof val === 'string') {
      date = parseDateOnlyString(val) ?? parseISO(val);
    } else if (typeof val === 'number') {
      // 10^11 ms is 1973. If less, it's likely seconds.
      const isSeconds = val < 100000000000;
      date = new Date(isSeconds ? val * 1000 : val);
    } else {
      return new Date();
    }

    if (isValid(date)) {
      // Safety Filter: Ignore 1970 (standard epoch-related bug in the app)
      if (date.getFullYear() === 1970) return new Date();
      return date;
    }
    return new Date();
  };

  const toDateOrNull = (
    val: Date | string | number | null | undefined,
  ): Date | null => {
    if (!val || val === 0) return null;
    if (val instanceof Date) return isValid(val) ? val : null;
    let date: Date;
    if (typeof val === 'string') {
      date = parseDateOnlyString(val) ?? parseISO(val);
    } else if (typeof val === 'number') {
      const isSeconds = val < 100000000000;
      date = new Date(isSeconds ? val * 1000 : val);
    } else {
      return null;
    }

    if (isValid(date)) {
      // Safety Filter: Ignore 1970
      if (date.getFullYear() === 1970) return null;
      return date;
    }
    return null;
  };

  const [currentMonth, setCurrentMonth] = useState(() => toDate(startDate));
  const [viewYear, setViewYear] = useState(currentMonth.getFullYear());
  const containerRef = useRef<HTMLDivElement>(null);

  const start = useRange ? toDateOrNull(startDate) : null;
  const end = useRange ? toDateOrNull(endDate) : null;
  const singleDate = asSingle ? toDateOrNull(startDate) : null;

  // Sync currentMonth when props change (crucial for API-loaded data)
  useEffect(() => {
    if (startDate) {
      setCurrentMonth(toDate(startDate));
    }
  }, [startDate]);

  const [localPickingStart, setLocalPickingStart] = useState<Date | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setLocalPickingStart(null);
        setViewMode('calendar');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updatePopupPosition = () => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const popupHeight = viewMode === 'calendar' ? 390 : 320;
    const popupWidth = 280;
    const viewportPadding = 12;
    const fitsBelow = rect.bottom + popupHeight + viewportPadding <= window.innerHeight;
    const top = fitsBelow
      ? rect.bottom + 8
      : Math.max(viewportPadding, rect.top - popupHeight - 8);
    const left = Math.min(
      Math.max(viewportPadding, rect.left),
      window.innerWidth - popupWidth - viewportPadding,
    );
    setPopupPosition({ top, left });
  };

  useEffect(() => {
    if (!isOpen) return;
    updatePopupPosition();
    window.addEventListener('resize', updatePopupPosition);
    window.addEventListener('scroll', updatePopupPosition, true);
    return () => {
      window.removeEventListener('resize', updatePopupPosition);
      window.removeEventListener('scroll', updatePopupPosition, true);
    };
  }, [isOpen, viewMode]);

  const handleDateClick = (date: Date) => {
    if (asSingle) {
      onChange({
        startDate: format(date, 'yyyy-MM-dd'),
        endDate: format(date, 'yyyy-MM-dd'),
      });
      setIsOpen(false);
      return;
    }

    if (useRange) {
      if (!localPickingStart) {
        setLocalPickingStart(date);
        onChange({ startDate: format(date, 'yyyy-MM-dd'), endDate: null });
      } else {
        if (isSameDay(date, localPickingStart)) {
          setLocalPickingStart(null);
          onChange({ startDate: null, endDate: null });
        } else if (date < localPickingStart) {
          onChange({
            startDate: format(date, 'yyyy-MM-dd'),
            endDate: format(localPickingStart, 'yyyy-MM-dd'),
          });
        } else {
          onChange({
            startDate: format(localPickingStart, 'yyyy-MM-dd'),
            endDate: format(date, 'yyyy-MM-dd'),
          });
        }
        setLocalPickingStart(null);
      }
    }
  };

  const renderCalendar = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDateCal = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDateCal = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const rows = [];
    let daysList = [];
    let day = startDateCal;

    while (day <= endDateCal) {
      for (let i = 0; i < 7; i++) {
        const d = day;
        const isCurrentMonth = isSameMonth(d, monthStart);

        let isSelected = false;
        let isInRange = false;

        if (asSingle && singleDate) {
          isSelected = isSameDay(d, singleDate);
        } else if (useRange) {
          isSelected =
            (start && isSameDay(d, start)) ||
            (end && isSameDay(d, end)) ||
            false;
          if (start && end) {
            isInRange = d > start && d < end;
          }
        }

        daysList.push(
          <div
              key={d.toISOString()}
              className={`
              relative w-8 h-8 flex items-center justify-center cursor-pointer rounded-lg text-[12px] transition-all duration-200
              ${
                !isCurrentMonth ? 'text-slate-300 opacity-50' : 'text-slate-600'
              }
              ${
                isSelected
                  ? 'bg-emerald-600 text-white font-bold shadow-md shadow-emerald-100 z-10'
                  : 'hover:bg-slate-50'
              }
              ${isInRange ? 'bg-emerald-50 text-emerald-700' : ''}
              ${
                isSameDay(d, new Date()) && !isSelected
                  ? 'text-emerald-600 font-bold'
                  : ''
              }
              ${start && isSameDay(d, start) && end ? 'rounded-r-none' : ''}
              ${end && isSameDay(d, end) && start ? 'rounded-l-none' : ''}
            `}
              onClick={() => handleDateClick(d)}
          >
            { format(d, 'd') }
            { useRange && start && isSameDay(d, start) && (
              <div className='absolute -top-4 left-1/2 -translate-x-1/2 text-[8px] font-black text-emerald-600 uppercase tracking-tighter bg-white px-1 border border-emerald-100 rounded shadow-sm whitespace-nowrap z-20'>
                From
              </div>
            ) }
            { useRange && end && isSameDay(d, end) && (
              <div className='absolute -top-4 left-1/2 -translate-x-1/2 text-[8px] font-black text-emerald-500 uppercase tracking-tighter bg-white px-1 border border-emerald-100 rounded shadow-sm whitespace-nowrap z-20'>
                To
              </div>
            ) }
          </div>,
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div key={day.toISOString()} className='grid grid-cols-7 gap-0.5'>
          { daysList }
        </div>,
      );
      daysList = [];
    }
    return rows;
  };

  const renderMonthPicker = () => {
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    return (
      <div className='grid grid-cols-3 gap-2 py-2'>
        { months.map((month, idx) => {
          const isSelected = currentMonth.getMonth() === idx;
          return (
            <button
                key={month}
                onClick={() => {
                  const newDate = new Date(currentMonth);
                  newDate.setMonth(idx);
                  setCurrentMonth(newDate);
                  setViewMode('calendar');
                }}
                className={`py-2 rounded-xl text-[12px] font-semibold transition-all ${
                  isSelected
                    ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-100'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
            >
              { month }
            </button>
          );
        }) }
      </div>
    );
  };

  const renderYearPicker = () => {
    const years = [];
    for (let i = viewYear - 4; i <= viewYear + 7; i++) {
      years.push(i);
    }
    return (
      <div className='grid grid-cols-3 gap-2 py-2'>
        { years.map((year) => {
          const isSelected = currentMonth.getFullYear() === year;
          return (
            <button
                key={year}
                onClick={() => {
                  const newDate = new Date(currentMonth);
                  newDate.setFullYear(year);
                  setCurrentMonth(newDate);
                  setViewYear(year);
                  setViewMode('month');
                }}
                className={`py-2 rounded-xl text-[12px] font-semibold transition-all ${
                  isSelected
                    ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-100'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
            >
              { year }
            </button>
          );
        }) }
      </div>
    );
  };

  const displayValue = () => {
    if (asSingle && singleDate && isValid(singleDate)) {
      return format(singleDate, 'MMM d, yyyy');
    }
    if (useRange) {
      if (start && isValid(start) && end && isValid(end)) {
        return `${format(start, 'MM/dd/yy')} - ${format(end, 'MM/dd/yy')}`;
      } else if (start && isValid(start)) {
        return `${format(start, 'MM/dd/yy')} - ...`;
      }
      return 'Please select range';
    }
    return 'Select Date';
  };

  return (
    <div className='w-full relative' ref={containerRef}>
      <style>{ `
        @keyframes datePickerEntrance {
          from { opacity: 0; transform: translateY(10px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .date-picker-popup {
          animation: datePickerEntrance 0.2s ease-out forwards;
        }
      ` }</style>
      { label && (
        <label className='block mb-1.5 text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-0.5'>
          { label }
        </label>
      ) }
      <div
          onClick={() => {
            if (disabled) return;
            updatePopupPosition();
            setIsOpen(!isOpen);
          }}
          className={`
          flex items-center gap-2.5 w-full h-10 px-3 bg-white border rounded-xl cursor-pointer transition-all duration-200
          ${
            isOpen
              ? 'border-emerald-500 ring-4 ring-emerald-500/10'
              : 'border-slate-200 hover:border-slate-300 shadow-sm'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed bg-slate-50' : ''}
        `}
      >
        <CalendarClockIcons className='w-4.5 h-4.5 text-slate-400' />
        <span
            className={`text-[13px] font-medium flex-1 truncate ${
              startDate || (useRange && !startDate)
                ? 'text-slate-800'
                : 'text-slate-400'
            }`}
        >
          { displayValue() }
        </span>
      </div>

      { isOpen && (
        <div
            className='date-picker-popup fixed z-[99999] w-[280px] overflow-visible rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl'
            style={{ top: popupPosition.top, left: popupPosition.left }}
        >
          <div className='flex items-center justify-between mb-4'>
            <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (viewMode === 'year') setViewYear((prev) => prev - 12);
                  else setCurrentMonth(subMonths(currentMonth, 1));
                }}
                className='w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-50 text-slate-400 transition-colors'
            >
              <ChevronLeft className='w-4 h-4' />
            </button>
            <div className='flex gap-1'>
              <button
                  onClick={() =>
                    setViewMode(viewMode === 'month' ? 'calendar' : 'month')
                  }
                  className='text-[13px] font-bold text-slate-900 hover:bg-slate-50 px-1.5 py-0.5 rounded-lg transition-colors'
              >
                { format(currentMonth, 'MMMM') }
              </button>
              <button
                  onClick={() =>
                    setViewMode(viewMode === 'year' ? 'calendar' : 'year')
                  }
                  className='text-[13px] font-bold text-slate-900 hover:bg-slate-50 px-1.5 py-0.5 rounded-lg transition-colors'
              >
                { format(currentMonth, 'yyyy') }
              </button>
            </div>
            <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (viewMode === 'year') setViewYear((prev) => prev + 12);
                  else setCurrentMonth(addMonths(currentMonth, 1));
                }}
                className='w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-50 text-slate-400 transition-colors'
            >
              <ChevronRight className='w-4 h-4' />
            </button>
          </div>

          { useRange && (
            <div className='mb-3 px-1 py-1 bg-slate-50 rounded-lg border border-slate-100 text-center'>
              <span className='text-[10px] font-bold text-slate-400 uppercase tracking-widest'>
                { !localPickingStart ? 'Select Start Date' : 'Select End Date' }
              </span>
            </div>
          ) }

          { viewMode === 'calendar' && (
            <>
              <div className='grid grid-cols-7 gap-0.5 mb-2'>
                { ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((d) => (
                  <div
                      key={d}
                      className='w-8 text-center text-[10px] font-bold text-slate-400 uppercase tracking-wider'
                  >
                    { d }
                  </div>
                )) }
              </div>
              <div className='flex flex-col gap-0.5'>{ renderCalendar() }</div>
            </>
          ) }

          { viewMode === 'month' && renderMonthPicker() }
          { viewMode === 'year' && renderYearPicker() }

          <div className='mt-4 pt-3 border-t border-slate-50 flex justify-end'>
            <button
                onClick={() => setIsOpen(false)}
                className='text-[11px] font-bold text-emerald-600 hover:text-emerald-700 px-2 py-1'
            >
              Close
            </button>
          </div>
        </div>
      ) }
    </div>
  );
};
